import React from 'react';
import { MapPin, Calendar, TrendingUp, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { usePortfolio } from '../context/PortfolioContext';

const Ventures = () => {
  const { ventures, loading } = usePortfolio();
  const [ref, isVisible] = useScrollAnimation(0.1);

  if (loading || !ventures.length) return null;

  return (
    <section id="ventures" className="py-24 md:py-32 bg-gray-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="max-w-3xl mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Ventures
          </h2>
          <p className="text-lg text-gray-700">
            Building technology companies that solve real-world problems and drive digital transformation across industries.
          </p>
        </div>

        {/* Ventures Grid */}
        <div ref={ref} className="space-y-8">
          {ventures.map((venture, index) => {
            return (
              <div
                key={venture.id}
                className={`${
                  isVisible ? 'animate-fade-in-up' : 'animate-on-scroll'
                }`}
                style={{ animationDelay: `${index * 200}ms` }}
              >
                <Card className="overflow-hidden border-gray-200 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
                  <div className="grid md:grid-cols-12 gap-0">
                    {/* Logo Section */}
                    <div className="md:col-span-3 bg-white flex items-center justify-center p-8 border-r border-gray-200">
                      <div className="w-full h-24 flex items-center justify-center">
                        <img
                          src={venture.logo}
                          alt={venture.name}
                          className="max-w-full max-h-full object-contain"
                          style={{ maxWidth: '180px', maxHeight: '96px' }}
                        />
                      </div>
                    </div>

                    {/* Content Section */}
                    <div className="md:col-span-9 p-8">
                      <CardHeader className="p-0 mb-6">
                        <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
                          <div>
                            <CardTitle className="text-2xl md:text-3xl mb-2">
                              {venture.name}
                            </CardTitle>
                            <CardDescription className="text-lg font-medium text-gray-700">
                              {venture.role}
                            </CardDescription>
                          </div>
                          <Badge variant="secondary" className="text-sm">
                            {venture.duration}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-gray-600 items-center">
                          <span className="flex items-center gap-2">
                            <MapPin size={16} />
                            {venture.location}
                          </span>
                          {venture.website && (
                            <a
                              href={venture.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-gray-900 hover:text-gray-600 transition-colors font-medium"
                            >
                              <ExternalLink size={16} />
                              Visit Website
                            </a>
                          )}
                        </div>
                      </CardHeader>

                      <CardContent className="p-0 space-y-6">
                        <p className="text-gray-700 leading-relaxed">
                          {venture.description}
                        </p>

                        {/* Impact Metrics */}
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            <TrendingUp size={16} />
                            Key Impact
                          </h4>
                          <div className="grid sm:grid-cols-2 gap-3">
                            {venture.impact.map((item, idx) => (
                              <div
                                key={idx}
                                className="flex items-start gap-2 text-sm text-gray-700"
                              >
                                <span className="text-gray-900 mt-0.5">•</span>
                                <span>{item}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </CardContent>
                    </div>
                  </div>
                </Card>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Ventures;